import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Trash2Icon } from "lucide-react";

import { useItemLabels, type ItemLabel } from "@/api/items";
import { DataTable, type ColumnDef } from "@/components/data-table/DataTable";
import { useDataTableState } from "@/components/data-table/useDataTableState";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { DeleteTagDialog } from "./DeleteTagDialog";
import { EditTagDialog } from "./EditTagDialog";
import { tagColorClass } from "./types";

export default function Tags() {
  const { t } = useTranslation();

  const ds = useDataTableState({
    defaultSort: { id: "name", desc: false },
    defaultPageSize: 50,
    filterDeps: [],
  });

  const query = useItemLabels({
    search: ds.debouncedSearch || undefined,
    ordering: ds.orderingParam,
  });

  const [editing, setEditing] = useState<ItemLabel | null>(null);
  const [deleting, setDeleting] = useState<ItemLabel | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const columns = useMemo<ColumnDef<ItemLabel>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("settings.tags.nameLabel"),
        enableSorting: true,
        cell: ({ row }) => (
          <span
            className={cn(
              "rounded-md px-2 py-0.5 text-sm font-medium",
              tagColorClass(row.original.color),
            )}
          >
            {row.original.name}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        size: 48,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon-sm"
              data-testid="delete"
              aria-label={t("common.delete")}
              onClick={() => setDeleting(row.original)}
            >
              <Trash2Icon className="text-muted-foreground size-4" />
            </Button>
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("settings.tags.title")}
        meta={
          <p className="text-muted-foreground text-sm">
            {t("settings.tags.description")}
          </p>
        }
        actions={
          <Button onClick={() => setNewOpen(true)}>
            {t("settings.tags.create")}
          </Button>
        }
      />

      <DataTable<ItemLabel>
        {...ds.tableProps}
        data={query.data ?? []}
        totalCount={query.data?.length ?? 0}
        isLoading={query.isLoading}
        columns={columns}
        searchPlaceholder={t("settings.tags.searchPlaceholder")}
        emptyLabel={t("settings.tags.empty")}
        getRowId={(r) => String(r.id)}
        onRowClick={(row) => setEditing(row)}
      />

      <EditTagDialog
        tag={editing}
        open={newOpen || editing !== null}
        onClose={() => {
          setNewOpen(false);
          setEditing(null);
        }}
      />
      <DeleteTagDialog tag={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}
